import { BackstageOverrides } from '@backstage/core-components';
import { alpha, Theme } from '@mui/material/styles';
import {
  getFocusRing,
  getSurfaceColor,
  getThemeTokens,
  radii,
  shellMetrics,
} from '../tokens';

export const styledBackstageSidebarItem = (
  theme: Theme,
): BackstageOverrides['BackstageSidebarItem'] => {
  const tokens = getThemeTokens(theme);
  const openItemWidth = `calc(100% - ${shellMetrics.railOpenInset * 2}px)`;
  const closedItemWidth = `calc(100% - ${shellMetrics.railClosedInset * 2}px)`;

  return {
    root: {
      alignItems: 'center',
      border: '1px solid transparent',
      borderRadius: radii.sm,
      boxSizing: 'border-box',
      color: theme.palette.navigation.color,
      display: 'flex',
      listStyle: 'none',
      margin: '3px 0',
      overflow: 'hidden',
      textDecoration: 'none',
      transition:
        'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, color 150ms ease',
      '&:focus-visible': {
        boxShadow: getFocusRing(theme),
        outline: 'none',
      },
      '&[aria-label="/search"]': {
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${tokens.navigationBorder}`,
        borderRadius: radii.md,
        minHeight: shellMetrics.railSearchHeight,
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
        width: `${openItemWidth} !important`,
      },
      '&[aria-label="/search"]:hover': {
        backgroundColor: getSurfaceColor(theme, 'raised'),
      },
    },
    open: {
      marginLeft: `${shellMetrics.railOpenInset}px`,
      marginRight: `${shellMetrics.railOpenInset}px`,
      minHeight: shellMetrics.railRowHeight,
      paddingLeft: `${shellMetrics.railItemPaddingX}px`,
      paddingRight: `${shellMetrics.railItemPaddingX}px`,
      width: `${openItemWidth} !important`,
    },
    closed: {
      marginLeft: `${shellMetrics.railClosedInset}px`,
      marginRight: `${shellMetrics.railClosedInset}px`,
      justifyContent: 'center',
      minHeight: shellMetrics.railRowHeight,
      width: `${closedItemWidth} !important`,
    },
    buttonItem: {
      alignItems: 'center',
      borderRadius: radii.sm,
      minHeight: shellMetrics.railRowHeight,
      paddingLeft: 0,
      paddingRight: 0,
      width: '100%',
    },
    highlightable: {
      '&:hover': {
        backgroundColor:
          theme.palette.navigation.navItem?.hoverBackground ??
          alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.navigation.selectedColor,
      },
    },
    highlighted: {
      backgroundColor: alpha(theme.palette.primary.light, 0.18),
    },
    label: {
      color: 'inherit',
      display: 'block',
      flex: '1 1 auto',
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 600,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    iconContainer: {
      alignItems: 'center',
      boxSizing: 'border-box',
      color: alpha(tokens.textSecondary, 0.95),
      display: 'flex',
      flex: '0 0 auto',
      justifyContent: 'center',
      marginRight: `${shellMetrics.railItemGap}px`,
      maxWidth: `${shellMetrics.railIconSlot}px`,
      minWidth: `${shellMetrics.railIconSlot}px`,
      width: `${shellMetrics.railIconSlot}px`,
      '[aria-label="/search"] > &': {
        backgroundColor: 'transparent',
        borderRadius: 0,
        color: tokens.textMuted,
        height: 20,
        marginRight: theme.spacing(1.25),
        maxWidth: 20,
        minWidth: 20,
        width: 20,
      },
    },
    searchRoot: {
      margin: `${shellMetrics.railDividerGap}px 0 ${
        shellMetrics.railDividerGap + 2
      }px`,
      width: '100%',
    },
    searchContainer: {
      backgroundColor: 'transparent',
      border: '0',
      borderRadius: 0,
      boxShadow: 'none',
      flex: '1 1 auto',
      minWidth: 0,
      width: 'auto',
      '& .MuiInput-underline::before, & .MuiInput-underline::after': {
        display: 'none',
      },
    },
    searchField: {
      color: tokens.textPrimary,
      display: 'flex',
      fontSize: theme.typography.body2.fontSize,
      minHeight: shellMetrics.railSearchHeight,
      paddingLeft: 0,
      paddingRight: 0,
    },
    searchFieldHTMLInput: {
      padding: '0 !important',
      '::placeholder': {
        color: tokens.textMuted,
        opacity: 1,
      },
    },
    secondaryAction: {
      alignItems: 'center',
      color: alpha(tokens.textMuted, 0.9),
      display: 'flex',
      flex: '0 0 auto',
      justifyContent: 'center',
      marginLeft: 'auto',
      minWidth: 0,
      '&:empty': {
        display: 'none',
      },
      '[aria-label="/search"] > &': {
        flex: '1 1 auto',
        marginLeft: 0,
      },
    },
    submenuArrow: {
      color: alpha(tokens.textMuted, 0.9),
    },
    closedItemIcon: {
      color: alpha(tokens.textSecondary, 0.88),
    },
    selected: {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
      borderLeft: '0 !important',
      boxShadow: 'none',
      color: theme.palette.navigation.selectedColor,
      '&[aria-label="/search"]': {
        backgroundColor: getSurfaceColor(theme, 'raised'),
        borderColor: alpha(theme.palette.primary.main, 0.42),
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
      },
    },
  };
};
